/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useContext, useState } from 'react';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { makeStyles } from '@mui/styles';
import { checkHasUserPermission } from 'helper/commonFunctions';
import GeneralEvents from 'events/GeneralEvents';
import { setupGuideStore } from 'components/layout/SetupGuide/store';
import { appCtx } from 'components/appStore';
import {
    gradeEmployerAnswer
} from 'requests/JobOpportunityRequests';

import Typography from '@mui/material/Typography';
import GradeCardNavigation from './components/GradeCardNavigation';
import TasksGradeBlock from './components/TasksGradeBlock';
import SimpleGradeBlock from './components/SimpleGradeBlock';
import ScreenList from './components/ScreenList';
import AuthDialog from '../../AuthDialog';

const useStyles = makeStyles(theme => ({
    wrapper: {
        position: 'relative',
        paddingLeft: 86,
        zIndex: 100,
        borderRadius: 4,
        borderTop: `2px solid ${theme.palette.grey[200]}`,
        borderBottom: `2px solid ${theme.palette.grey[200]}`,
        borderRight: `2px solid ${theme.palette.grey[200]}`,
        minHeight: ({ totalCount }) => totalCount * 70 + 60
    },
    wrapperExtented: {
        paddingLeft: 170
    }
}));


const GradeBlock = observer(({
    candidate, audition, updateUserAuditionAndBox,
    gradeFunction = gradeEmployerAnswer, hideCandidateDetails,
    gradeableCard, propPublic, authorizeGuest, isIntegration, gradeProps, userAudition
}) => {
    const [gradeLoading, setGradeLoading] = useState(false);
    const [dialogAuthOpen, setDialogAuthOpen] = useState(false);
    const [unSavedGrade, setUnSavedGrade] = useState(null);

    const { screenQuestionResults, currentQuestion, questionResults, gotoQuestion,
        currentGrade, questionNumber, setGradeQuestion, screenSelected, setScreenSelected, taskNumber, setTaskNumber,
        setQuestionResultIndex, returnCurrentTask, setCurrentQuestion, setQuestionResults
    } = gradeProps;

    const { company, flashMessage } = useContext(appCtx);

    const classes = useStyles({
        totalCount: Math.max((questionResults.length + (screenQuestionResults.length ? 1 : 0)),
		currentQuestion?.candidateTasks?.length || 0)
    });

    const { candidateId, userAuditionId, candidateUuid, fullName } = candidate;
    const { uuid: ttId, name: ttName, permissions, screeningChatbotEnabled = null } = audition;
    const showTasks = Boolean(currentQuestion?.candidateTasks?.length);

    const openAuthorisationDialog = (notSavedGrade) => {
        setDialogAuthOpen(true);
        setUnSavedGrade(notSavedGrade);
    };

    const closeAuthorisationDialog = () => {
        setDialogAuthOpen(false);
        setGradeLoading(false);
    };

    const saveGrade = (value, taskQuestionResultId = null, gradeBlockIndex = 0) => {
        let questionResultId = currentQuestion.id;
        const currentTask = returnCurrentTask(currentQuestion);
        if (currentTask) {
            questionResultId = currentTask.questionResults[gradeBlockIndex].id;
        }
        setGradeLoading(taskQuestionResultId || true);

        if (propPublic && !localStorage.getItem('token')) {
            openAuthorisationDialog(value);
            return;
        }

        return gradeFunction(questionResultId, value)
            .then(({ success, data }) => {
                setGradeLoading(false);
                if (success) {
                    addGradeEvent(value, gradeBlockIndex);
                    setupGuideStore.updateGradeInfo();
                    if (updateUserAuditionAndBox) updateUserAuditionAndBox(candidateId);
                    if (currentTask) {
                        setQuestionResultIndex(gradeBlockIndex);
                        const newCurrentQuestion = { ...currentQuestion };
                        newCurrentQuestion.candidateTasks[taskNumber].questionResults[gradeBlockIndex] = data;
                        setCurrentQuestion(newCurrentQuestion);
                    } else {
                        setGradeQuestion(data);
                        updateInQuestionsList(questionResultId, data);
                    }
                }
            })
            .catch((err) => {
                setGradeLoading(false);
                flashMessage(err?.response?.status === 403
                    ? 'You don’t have permission for this action.'
                    : 'Something went wrong',
                'error');
            });
    };

    const updateInQuestionsList = (questionResultId, data) => {
        const foundIndex = questionResults.findIndex(({ id }) => id === questionResultId);
        if (foundIndex === -1) return;
        const newQuestionResults = [...questionResults];
        newQuestionResults[foundIndex].manualGradeMissing = data.manualGradeMissing;
        setQuestionResults(newQuestionResults);
    };

    const addGradeEvent = (scoreGiven, questionIndex) => {
        if (!currentQuestion) return;
        const { question: { uuid: questionId } } = currentQuestion;
        const currentTask = returnCurrentTask(currentQuestion);
        GeneralEvents.QUESTION_SCORED({
            scoreGiven,
            candidateId: candidateUuid,
            questionNumber: questionNumber + 1,
            questionId,
            taskId: currentTask?.questionResults[questionIndex].question.uuid,
            ttId,
            ttName,
            context: 'grading assessment candidate card'
        });
    };

    const canGrade = checkHasUserPermission(company, permissions, 'grade');


    if (!questionResults.length && !screenQuestionResults.length) return null;

    const commonProps = {
        candidate,
        audition,
        updateUserAuditionAndBox,
        gradeableCard,
        propPublic,
        gotoQuestion,
        isIntegration,
        saveGrade,
        gradeLoading,
        canGrade,
        currentGrade,
        currentQuestion,
        fullName,
        questionResults,
        questionNumber,
        setGradeQuestion,
        setGradeLoading
    };

    return (
        <div>
            <Typography className="u-mrg--bx2 u-mrg--tx4" variant="h5">Candidate Answers</Typography>
            <div className={clsx(classes.wrapper, showTasks && classes.wrapperExtented)}>
                <GradeCardNavigation
                    questions={questionResults}
                    questionNumber={questionNumber}
                    gotoQuestion={gotoQuestion}
                    propPublic={propPublic}
                    screenSelected={screenSelected}
                    setScreenSelected={setScreenSelected}
                    hasScreenQuestions={Boolean(screenQuestionResults.length)}
                    company={company}
                />
                {
                    screenSelected ? (
                        <ScreenList
                            screenQuestionResults={screenQuestionResults}
                            userAuditionId={propPublic ? userAudition?.uuid : userAuditionId}
                            screeningChatbotEnabled={screeningChatbotEnabled}
                            hideCandidateDetails={hideCandidateDetails}
                            isPublic={propPublic}
                        />
                    ) : (
                        <div>
                            {
                                showTasks ? (
                                    <TasksGradeBlock
                                        taskNumber={taskNumber}
                                        setTaskNumber={setTaskNumber}
                                        {...commonProps}
                                    />
                                ) : (
                                    <SimpleGradeBlock {...commonProps} />
                                )
                            }
                        </div>
                    )
                }
            </div>
            <AuthDialog
                open={dialogAuthOpen}
                notSavedValue={unSavedGrade}
                saveValue={saveGrade}
                closeDialog={closeAuthorisationDialog}
                candidateName={fullName}
                flashMessage={flashMessage}
                authorizeGuest={authorizeGuest}
            />
        </div>
    );
});

export default GradeBlock;
